import type { MotivoSinTramosClp } from '../../services/main-rates.service';
import { ceil2 } from './tramos-usdt-editor.rules';
import { floor2 } from './tramos-usdt-ves-editor.rules';

/**
 * Reglas puras de las pestañas CLP→USDT (compra) y USDT→CLP (venta) del modal
 * «Tasas por monto» (22/10/2026). Sin Angular ni HTTP.
 *
 * El motor de tramos es el MISMO de VES (`tramos-usdt-editor.rules` para la
 * compra, `tramos-usdt-ves-editor.rules` para la venta). Lo único propio de
 * CLP es el límite, que acá (como en el back, `tramos-clp-usdt.calculo.ts`)
 * se calcula y se le pasa al motor como un margen mínimo EFECTIVO sobre el
 * spot:
 *
 *   Compra:  PISO  = max( ceil2(spot·(1+m)), ceil2(base·(1−tope)), ceil2(baseVenta·1,005) )
 *   Venta:   TECHO = min( floor2(spot·(1−m)), floor2(base·(1+tope)), floor2(compraMinFinal/1,005) )
 *
 * La referencia es `PRECIO_SPOT` (spot USD/CLP). El back es la autoridad: su
 * vista previa reemplaza a esta estimación en cuanto llega.
 */

/** Tope % máximo que acepta el back (más es un error de tipeo). */
export const TOPE_MAX_PCT = 5;
/** Colchón % de ida y vuelta entre compra y venta (igual que el back). */
export const MARGEN_IDA_VUELTA_CLP_PCT = 0.5;
/** Monto de ejemplo del simulador de la compra, en CLP. */
export const MONTO_SIM_CLP = 1_000_000;

const positivo = (v: unknown): number | null => {
  const n = Number(v);
  return v != null && v !== '' && Number.isFinite(n) && n > 0 ? n : null;
};

const pct = (v: unknown): number | null => {
  const n = Number(v);
  return v != null && v !== '' && Number.isFinite(n) && n >= 0 ? n : null;
};

/** PISO de la compra (sin recortar a la base). Null si faltan spot, base, margen o tope. */
export function pisoClpUsdt(p: {
  spot: number | null | undefined;
  base: number | null | undefined;
  margenMinPct: number | null | undefined;
  topePct: number | null | undefined;
  baseVenta?: number | null;
}): number | null {
  const spot = positivo(p.spot);
  const base = positivo(p.base);
  const m = pct(p.margenMinPct);
  const tope = pct(p.topePct);
  if (spot === null || base === null || m === null || tope === null) return null;
  const bv = positivo(p.baseVenta);
  return Math.max(
    ceil2(spot * (1 + m / 100)),
    ceil2(base * (1 - tope / 100)),
    bv === null ? 0 : ceil2(bv * (1 + MARGEN_IDA_VUELTA_CLP_PCT / 100)),
  );
}

/** TECHO de la venta (sin recortar a la base). Null si faltan spot, base, margen o tope. */
export function techoUsdtClp(p: {
  spot: number | null | undefined;
  base: number | null | undefined;
  margenMinPct: number | null | undefined;
  topePct: number | null | undefined;
  compraMinFinal?: number | null;
}): number | null {
  const spot = positivo(p.spot);
  const base = positivo(p.base);
  const m = pct(p.margenMinPct);
  const tope = pct(p.topePct);
  if (spot === null || base === null || m === null || tope === null) return null;
  const c = positivo(p.compraMinFinal);
  return Math.min(
    floor2(spot * (1 - m / 100)),
    floor2(base * (1 + tope / 100)),
    c === null ? Infinity : floor2(c / (1 + MARGEN_IDA_VUELTA_CLP_PCT / 100)),
  );
}

/** Margen % sobre el spot que, pasado al motor de compra, da exactamente `piso`. */
export function margenEfectivoCompra(piso: number | null, spot: number | null | undefined): number | null {
  const s = positivo(spot);
  if (piso == null || s === null) return null;
  return Math.max(0, (piso / s - 1) * 100);
}

/** Margen % bajo el spot que, pasado al motor de venta, da exactamente `techo`. */
export function margenEfectivoVenta(techo: number | null, spot: number | null | undefined): number | null {
  const s = positivo(spot);
  if (techo == null || s === null) return null;
  return Math.max(0, (1 - techo / s) * 100);
}

/** Error del campo tope; null si está bien (número de 0 a 5). */
export function errorTope(v: unknown): string | null {
  const n = Number(v);
  if (v == null || v === '' || !Number.isFinite(n) || n < 0 || n > TOPE_MAX_PCT) {
    return `Número de 0 a ${TOPE_MAX_PCT}.`;
  }
  return null;
}

/** Por qué hoy los tramos no rigen (mismos textos que la vista previa del back). Null si rigen. */
export function textoMotivoSinTramos(motivo: MotivoSinTramosClp | string | null | undefined, esVenta: boolean): string | null {
  const todos = esVenta ? 'todos los tramos pagan la base' : 'todos los tramos cobran la base';
  switch (motivo) {
    case 'MANUAL':
      return `La tasa base es manual: ${todos} mientras siga así.`;
    case 'FUENTE_CONGELADA':
      return 'La tasa base no se actualiza hace rato: los tramos no rigen hasta que vuelva el cron.';
    case 'SPOT_INVALIDO':
      return 'No hay PRECIO_SPOT válido: los tramos no rigen.';
    case 'SPOT_FUERA_DE_BANDA':
      return 'La base está a más de 3 % del spot: los tramos no rigen hasta que se normalice.';
    case 'SIN_BASE':
      return 'No hay tasa base.';
    default:
      return null;
  }
}

/** «Tope: 0,40 % → 0,50 %» para la lista de cambios; null si no cambió. */
export function cambioTope(antes: number | null | undefined, despues: number | null | undefined): string | null {
  if (Number(antes) === Number(despues)) return null;
  const f = (v: number | null | undefined) =>
    v == null || v === ('' as any) || !Number.isFinite(Number(v))
      ? '—'
      : `${Number(v).toLocaleString('es-CL', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} %`;
  return `Tope: ${f(antes)} → ${f(despues)}`;
}
